import { Box, Stack, Typography, useTheme, useMediaQuery } from '@mui/material'
import { TestParameter } from '../../../../types/TestParameter'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { ArrowIcon } from '../../../../components/icons'

const TEST_STRIP_BOXES = Array.from({ length: 16 }, (_, i) => i + 1)

interface ManualUploadHeaderProps {
  selectedValues: Record<string, number>
  parameters: TestParameter[]
}

export const ManualUploadHeader = ({ 
  selectedValues,
  parameters,
}: ManualUploadHeaderProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getBoxColor = (boxIndex: number) => {
    const parameter = parameters[boxIndex - 1]
    if (!parameter) return '#E6EEF4'

    const selectedValue = selectedValues[parameter.name]
    if (selectedValue === undefined) return '#E6EEF4'

    const valueObj = parameter.values.find(v => v.value === selectedValue)
    return valueObj ? valueObj.color : '#E6EEF4'
  }

  return (
    <Stack spacing={1}>
      {isMobile && (
        <Typography 
          color="text.primary" 
          fontSize="12px"
          fontWeight={600}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          Start here <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
        </Typography>
      )}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          '&::after': isMobile ? {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '40px',
            background: 'linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))',
            pointerEvents: 'none',
          } : {}
        }}
      >
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          justifyContent={{ xs: "flex-start", md: "center" }}
        >
          {!isMobile && (
            <Typography 
            color="text.primary" 
            fontSize="14px"
            fontWeight={600}
            sx={{ 
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              gap: '7px', 
              mr: 1
            }}
          >
            Start here <ArrowIcon />
          </Typography>
          
          )}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              backgroundColor: 'white',
              p: 1,
              pr: { xs: 4, md: 12 },
              borderRadius: 1,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              width: '100%',
            }}
          >
            {TEST_STRIP_BOXES.map((number) => (
              <Box
                key={number}
                sx={{
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 0.5,
                  backgroundColor: getBoxColor(number),
                  color: selectedValues[parameters[number - 1]?.name] !== undefined
                    ? 'white'
                    : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: getBoxColor(number),
                    filter: 'brightness(0.95)',
                  },
                  fontSize: { xs: '13px', md: '14px' },
                  flexShrink: 0,
                }}
              >
                {number}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  )
} 